/**
 * How much of each AI model's output reaches the client — a unit grid
 * (2026-09-08).
 *
 * REPLACES a grouped bar of the same table's numbers. Ramos: "this is a bar
 * chart that just dumps data on screen, we can do better." He was right for a
 * concrete reason: the counts here are 0, 1, 4 and 5 out of 26, and at that
 * scale bar LENGTH carries almost nothing — a bar of 1/26 is a sliver
 * indistinguishable from a bar of 2/26, and a bar of 0 is nothing at all.
 *
 * THE ARGUMENT. Every square is one real answer that model produced. Filled
 * squares are answers that surfaced the client; the brightest are answers that
 * recommended them. So the scarcity is countable rather than estimated from a
 * length, and a model that never surfaced the client is a full row of empty
 * squares — which states the finding far more plainly than a zero-length bar.
 *
 * On the Antibodies Inc run that reads: Gemini 26 empty squares, GPT one filled
 * square of 26, Sonnet 5 of 26 with 2 recommended, Perplexity 4 of 26 with 2.
 *
 * Recommendation is a strict subset of visibility (visibility is the union of
 * the three signals), so nesting the two shades is sound. Mention and citation
 * are NOT nested and are deliberately not drawn here — they overlap, and
 * shading them would assert a containment that does not hold.
 */
import type { ModelBreakdownEntry } from '@aurora/shared';
import { OUTCOME_RAMP, TARGET } from './palette';

const CELL = 13;
const GAP = 3;
const PER_ROW = 26;

const shortModel = (m: string): string => m.split('/').pop() ?? m;

export function ModelReachGrid({
  entries,
  targetName,
}: {
  entries: ModelBreakdownEntry[];
  targetName: string;
}): JSX.Element | null {
  if (entries.length === 0) return null;

  const visibleOf = (m: ModelBreakdownEntry): number =>
    m.visible ??
    Math.max(m.mention ?? 0, m.citation ?? 0, m.recommendation ?? 0);

  const blind = entries.filter((m) => visibleOf(m) === 0);
  const best = [...entries].sort((a, b) => visibleOf(b) - visibleOf(a))[0];

  return (
    <figure className="model-reach">
      {entries.map((m) => {
        const cells = m.scoredCells || 0;
        const visible = visibleOf(m);
        const recommended = Math.min(m.recommendation ?? 0, visible);
        const perRow = Math.min(PER_ROW, Math.max(1, cells));
        const rows = Math.ceil(cells / perRow);
        const w = perRow * (CELL + GAP);
        const h = rows * (CELL + GAP);
        return (
          <div key={m.model} className="model-reach-row">
            <div className="model-reach-label">
              <span className="model-reach-name">{shortModel(m.model)}</span>
              <span
                className={
                  visible === 0
                    ? 'model-reach-count is-zero'
                    : 'model-reach-count'
                }
              >
                {visible} of {cells} answers
                {recommended > 0 ? ` · ${recommended} recommended` : ''}
              </span>
            </div>
            <svg
              width={w}
              height={h}
              viewBox={`0 0 ${w} ${h}`}
              role="img"
              aria-label={`${shortModel(m.model)}: ${targetName} appeared in ${visible} of ${cells} answers, recommended in ${recommended}`}
            >
              {Array.from({ length: cells }, (_, i) => {
                const cx = (i % perRow) * (CELL + GAP);
                const cy = Math.floor(i / perRow) * (CELL + GAP);
                // Fill order: recommended first, then merely-surfaced, then the
                // answers that never named them.
                const state =
                  i < recommended
                    ? 'recommended'
                    : i < visible
                      ? 'visible'
                      : 'absent';
                return (
                  <rect
                    key={i}
                    x={cx}
                    y={cy}
                    width={CELL}
                    height={CELL}
                    rx={2.5}
                    className={`reach-cell is-${state}`}
                    fill={
                      state === 'recommended'
                        ? OUTCOME_RAMP.recommendation
                        : state === 'visible'
                          ? TARGET
                          : 'transparent'
                    }
                  >
                    <title>
                      {state === 'recommended'
                        ? `Recommended ${targetName}`
                        : state === 'visible'
                          ? `Surfaced ${targetName}`
                          : `Did not name ${targetName}`}
                    </title>
                  </rect>
                );
              })}
            </svg>
          </div>
        );
      })}

      <div className="chart-legend">
        <span className="chart-legend-item">
          <span
            className="chart-legend-swatch"
            style={{ background: OUTCOME_RAMP.recommendation }}
          />
          Recommended you
        </span>
        <span className="chart-legend-item">
          <span className="chart-legend-swatch" style={{ background: TARGET }} />
          Surfaced you
        </span>
        <span className="chart-legend-item">
          <span className="chart-legend-swatch reach-swatch-empty" />
          Did not name you
        </span>
      </div>

      <figcaption className="chart-verdict">
        Every square is one answer that model actually produced.{' '}
        {blind.length > 0 ? (
          <>
            <strong>
              {blind.length === 1
                ? `${shortModel(blind[0]?.model ?? '')} never surfaced you`
                : `${blind.length} of ${entries.length} models never surfaced you`}
            </strong>{' '}
            — an entirely empty row means every answer that model gave went to
            someone else.
            {best && visibleOf(best) > 0 && (
              <>
                {' '}
                Your best coverage is {shortModel(best.model)}, at{' '}
                {visibleOf(best)} of {best.scoredCells}.
              </>
            )}
          </>
        ) : (
          <>
            Every model in the panel surfaced you at least once; the filled count
            is how far into each model&apos;s output you reach.
          </>
        )}
      </figcaption>
    </figure>
  );
}
